/**
 * useServicePlans Hook
 *
 * Real-time hook for fetching service plans from Firestore
 * Used in intake forms, quote builder, and anywhere plans need to be displayed
 *
 * Features:
 * - Real-time updates via onSnapshot
 * - Filter by service type
 * - Active plans only option
 * - Promoted plans first sorting
 * - Multi-tenancy support
 */

import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '@/firebase';
import type { ServicePlan } from '@/types/schema';
import { useTenant } from './useTenant';

interface UseServicePlansOptions {
  // Filter by service type. Pass an array to query multiple types in one go
  // (used by the "4G/5G" UI option which spans both '4G Mobile' and '4G Data').
  serviceType?: ServicePlan['serviceType'] | ServicePlan['serviceType'][];
  activeOnly?: boolean; // Only return active plans (default: true)
  sortByPromoted?: boolean; // Sort promoted plans first (default: true)
  /**
   * Open the Firestore listener (default: true). Pass `false` from a component
   * that stays mounted but only needs plans some of the time — e.g. a globally
   * mounted modal that reads plans on one tab. Without it that component holds
   * a `service_plans` snapshot listener open for the whole session.
   */
  enabled?: boolean;
}

interface UseServicePlansReturn {
  plans: ServicePlan[];
  loading: boolean;
  error: Error | null;
}

/**
 * Hook to fetch service plans with real-time updates
 */
export function useServicePlans(options: UseServicePlansOptions = {}): UseServicePlansReturn {
  const { serviceType, activeOnly = true, sortByPromoted = true, enabled = true } = options;
  const { tenant } = useTenant();

  const [plans, setPlans] = useState<ServicePlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    if (!tenant?.id) {
      console.log('[useServicePlans] No tenant ID, skipping query');
      setLoading(false);
      return;
    }

    try {
      console.log('[useServicePlans] Query params:', {
        tenantId: tenant.id,
        serviceType,
        activeOnly,
        sortByPromoted
      });

      // Build query - NOTE: isActive filter done in-memory to avoid index requirement
      let q = query(
        collection(db, 'service_plans'),
        where('tenantId', '==', tenant.id)
      );

      // Filter by service type if provided. Array form uses Firestore 'in'
      // (max 30 values) so a single UI option can span multiple Firestore types.
      if (Array.isArray(serviceType)) {
        if (serviceType.length > 0) {
          q = query(q, where('serviceType', 'in', serviceType));
        }
      } else if (serviceType) {
        q = query(q, where('serviceType', '==', serviceType));
      }

      // Order by service type and sort order
      q = query(q, orderBy('serviceType', 'asc'), orderBy('sortOrder', 'asc'));

      // Subscribe to real-time updates
      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          console.log('[useServicePlans] Query returned', snapshot.size, 'plans');
          let plansData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          })) as ServicePlan[];

          // Filter by active status in-memory (avoids compound index requirement)
          if (activeOnly) {
            plansData = plansData.filter(p => p.isActive === true);
            console.log('[useServicePlans] After activeOnly filter:', plansData.length, 'plans');
          }

          console.log('[useServicePlans] Plans data:', plansData.map(p => ({
            displayName: p.displayName,
            serviceType: p.serviceType,
            isActive: p.isActive,
            isPromoted: p.isPromoted
          })));

          // Sort promoted plans first if requested
          if (sortByPromoted) {
            plansData.sort((a, b) => {
              // Promoted plans first
              if (a.isPromoted && !b.isPromoted) return -1;
              if (!a.isPromoted && b.isPromoted) return 1;
              // Then by sortOrder
              return (a.sortOrder || 999) - (b.sortOrder || 999);
            });
          }

          setPlans(plansData);
          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error('[useServicePlans] Error fetching service plans:', err);
          console.error('[useServicePlans] Error details:', err.message, err.code);
          setError(err as Error);
          setLoading(false);
        }
      );

      // Cleanup subscription on unmount
      return unsubscribe;
    } catch (err) {
      console.error('Error setting up service plans listener:', err);
      setError(err as Error);
      setLoading(false);
    }
    // Stable string key for the serviceType dep — array identity changes each
    // render even when contents match, so derive a deterministic key.
     
  }, [tenant?.id, Array.isArray(serviceType) ? serviceType.join(',') : serviceType, activeOnly, sortByPromoted, enabled]);

  return { plans, loading, error };
}

/**
 * Hook to fetch all service plans (including inactive)
 * Useful for admin pages
 *
 * @param enabled - pass false to hold the listener closed (see UseServicePlansOptions.enabled)
 */
export function useAllServicePlans(enabled = true): UseServicePlansReturn {
  return useServicePlans({ activeOnly: false, sortByPromoted: false, enabled });
}

/**
 * Hook to fetch active plans for a specific service type
 * Most common use case for intake forms
 */
export function useServicePlansByType(
  serviceType: ServicePlan['serviceType']
): UseServicePlansReturn {
  return useServicePlans({ serviceType, activeOnly: true, sortByPromoted: true });
}

/**
 * Hook to fetch promoted plans only
 * Useful for highlighting featured offerings
 */
export function usePromotedPlans(): UseServicePlansReturn {
  const { tenant } = useTenant();
  const [plans, setPlans] = useState<ServicePlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!tenant?.id) {
      setLoading(false);
      return;
    }

    try {
      const q = query(
        collection(db, 'service_plans'),
        where('tenantId', '==', tenant.id),
        where('isActive', '==', true),
        where('isPromoted', '==', true),
        orderBy('sortOrder', 'asc')
      );

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const plansData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          })) as ServicePlan[];

          setPlans(plansData);
          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error('Error fetching promoted plans:', err);
          setError(err as Error);
          setLoading(false);
        }
      );

      return unsubscribe;
    } catch (err) {
      console.error('Error setting up promoted plans listener:', err);
      setError(err as Error);
      setLoading(false);
    }
  }, [tenant?.id]);

  return { plans, loading, error };
}
